import {
  useGetCatalogConfig,
  useGetSyncStatus,
  useGetCatalogChanges,
  useGetCatalogShopSettings,
  useUpdateCatalogShopSettings,
  useGetCafe24Categories,
  useDeleteCategoryMapping,
  useSaveCategoryMapping,
  useStartSync,
  useGetProducts,
  useToggleProductExclude,
  getGetCatalogChangesQueryKey,
  getGetCafe24CategoriesQueryKey,
  getGetProductsQueryKey,
  getGetSyncStatusQueryKey,
} from '@/api/generated/v2-catalog/v2-catalog'
import type {
  Cafe24CategoriesResponse as Cafe24CategoriesResponse,
  Cafe24ProductResponse as Cafe24ProductResponse,
  CatalogChangesResponse,
  CatalogConfigResponse,
  CatalogProductsListResponse as CatalogProductsListResponse,
  CatalogShopSettingsResponse,
  CatalogSyncStatusResponse,
} from '@/api/schemas'
// CategoryMappingModal이 @/types의 Cafe24CategoryTreeNode을 사용하므로 동일 소스에서 import
import type { Cafe24CategoryTreeNode } from '@/types'
import { CategoryMappingModal } from '@/features/catalog/CategoryMappingModal'
import {
  CatalogSyncBanner,
  normalizeSyncStatus,
} from '@/features/catalog/CatalogSyncBanner'
import {
  CatalogStatusBadge,
  resolveDisplayStatus,
} from '@/features/catalog/CatalogStatusBadge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import { Dialog, DialogContent } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  Check,
  FolderTree,
  Info,
  Loader2,
  Package,
  Pencil,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

// 상태 탭 필터 값 (서버사이드 status 파라미터와 연동). 미지정=전체.
// not_selling=판매중지(selling=F만 서버사이드 필터), excluded=수동 제외.
// 주의: 탭(필터)과 배지(노출제한=품절+판매중지 상태)는 별개 개념이다.
// 백엔드 서버사이드 필터가 품절을 못 거르므로(Phase2), 탭은 정직하게 '판매중지'(selling=F)로 둔다.
type CatalogStatusFilter = 'not_selling' | 'excluded'

// URL 검색 파라미터 타입 정의
interface CatalogSearchParams {
  q?: string // 검색어
  page?: number // 페이지 번호 (1부터 시작)
  status?: CatalogStatusFilter // 상태 탭 (전체=undefined)
}

export const Route = createFileRoute('/_dashboard/catalog/')({
  component: CatalogProductsPage,
  validateSearch: (search: Record<string, unknown>): CatalogSearchParams => {
    return {
      q: typeof search.q === 'string' ? search.q : undefined,
      page: typeof search.page === 'number' ? search.page : undefined,
      // not_selling/excluded 외 값은 전체(undefined)로 정규화
      status:
        search.status === 'not_selling' || search.status === 'excluded'
          ? search.status
          : undefined,
    }
  },
})

function CatalogProductsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // shop 은 레이아웃이 useGetShop 으로 채운 컨텍스트에서 읽는다 (여기서 재조회하지 않는다)
  const { shop, setCatalogConnected } = useShop()
  const { q: urlSearch, page: urlPage, status: statusFilter } = Route.useSearch()

  // URL 파라미터로 초기값 설정
  const [search, setSearch] = useState(urlSearch || '')
  // 디바운스된 검색어 — 실제 상품 목록 쿼리는 이 값으로 실행한다 (Cafe24 호출 절감)
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch || '')
  const [offset, setOffset] = useState(urlPage ? (urlPage - 1) * 20 : 0)
  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstSearchRender = useRef(true)
  const limit = 20

  // 현재 페이지 번호 (1부터 시작)
  const currentPage = Math.floor(offset / limit) + 1

  // 동기화 UI 상태 (서버 폴링과 별개로 버튼 상태 관리)
  const [syncPending, setSyncPending] = useState(false) // API 호출 중 (버튼 클릭 -> 서버 running 확인까지)
  const [syncStartedAt, setSyncStartedAt] = useState<number | null>(null) // 동기화 시작 시간 (최소 유지 시간 체크용)

  // 브랜드명 편집 상태
  const [defaultBrandName, setDefaultBrandName] = useState('')

  // 카테고리 매핑 저장 중 상태 (로딩 스피너용)
  const [savingCategories, setSavingCategories] = useState<Set<number>>(
    new Set(),
  )

  // 인라인 브랜드명 편집 상태
  const [editingBrandName, setEditingBrandName] = useState(false)
  const [editBrandNameValue, setEditBrandNameValue] = useState('')

  // 설정 모달 상태
  const [showCategoryMappingModal, setShowCategoryMappingModal] =
    useState(false)
  const [showFullSyncConfirm, setShowFullSyncConfirm] = useState(false)

  // 제외 토글 상태 (로딩 중인 상품번호 Set)
  const [togglingProducts, setTogglingProducts] = useState<Set<number>>(
    new Set(),
  )

  // 낙관적 제외 상태 오버라이드 (API 응답 전 즉시 UI 반영)
  const [optimisticExclude, setOptimisticExclude] = useState<Map<number, boolean>>(new Map())

  // 제외 수 로컬 보정값 (optimistic update용)
  const [excludedDelta, setExcludedDelta] = useState(0)

  // ==================== TanStack Query 훅 ====================

  // 카탈로그 설정 조회 (연동 여부 확인용)
  const { data: configData } = useGetCatalogConfig({
    query: { enabled: !!shop },
  })
  // custom-instance가 JSON body를 직접 반환하므로 data 래퍼 없이 캐스팅
  const config = (configData ?? undefined) as CatalogConfigResponse | undefined

  // 동기화 상태 및 이력 조회 (동기화 중일 때 3초 폴링)
  const { data: syncStatusData } = useGetSyncStatus(
    { limit: 5 },
    {
      query: {
        enabled: !!shop,
        // 동기화 진행 중이면 3초마다 폴링
        refetchInterval: (query) => {
          const currentStatus = (query.state.data as CatalogSyncStatusResponse | undefined)?.config?.meta_catalog_sync_status
          return syncPending || currentStatus === 'running' ? 3000 : false
        },
      },
    },
  )
  const syncStatus = (syncStatusData ?? null) as CatalogSyncStatusResponse | null

  // 변경 감지 조회
  const { data: changesData, isFetched: changesLoaded } = useGetCatalogChanges({
    query: { enabled: !!shop },
  })
  const changes = changesData as CatalogChangesResponse | undefined
  const changedCount = changes?.changed_count ?? 0
  const customUnsyncedCount = changes?.custom_unsynced_count ?? 0
  const lastSyncAt = changes?.last_sync_at ?? null
  const syncNeeded = changes?.sync_needed ?? false
  const syncReason = changes?.sync_reason ?? null

  // Shop 설정 조회
  const { data: shopSettingsData } = useGetCatalogShopSettings({
    query: {
      enabled: !!shop,
    },
  })
  const shopSettings = (shopSettingsData ?? null) as CatalogShopSettingsResponse | null

  // Shop 설정 업데이트 mutation
  const updateShopSettingsMutation = useUpdateCatalogShopSettings()
  const savingSettings = updateShopSettingsMutation.isPending

  // Cafe24 카테고리 트리 조회
  const { data: categoriesData, isLoading: loadingCategories } = useGetCafe24Categories({
    query: { enabled: !!shop },
  })
  const categoriesPayload = (categoriesData ?? undefined) as Cafe24CategoriesResponse | undefined
  // API 스키마와 @/types의 Cafe24CategoryTreeNode은 구조적으로 호환 (children optional vs required)
  const categoryTree = (categoriesPayload?.tree ?? []) as Cafe24CategoryTreeNode[]

  // 검색 디바운스 (800ms) — 한글 IME 조합 중 중간 글자로 검색되지 않도록 여유를 둔다.
  // 검색어가 바뀌면 항상 1페이지(offset=0)로 리셋한다 (다른 페이지를 보던 중 검색 시 빈 페이지 방지).
  // 첫 렌더는 건너뛴다 — URL의 page 딥링크(offset 초기값)를 보존하기 위함.
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false
      return
    }
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current)
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
      setOffset(0)
    }, 800)
    return () => {
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current)
    }
  }, [search])

  // 상품 목록 조회
  const { data: productsData, isLoading: loadingProducts } = useGetProducts(
    {
      offset,
      limit,
      search: debouncedSearch || undefined,
      // 상태 탭 서버사이드 필터 (전체=undefined)
      status: statusFilter,
    },
    {
      query: { enabled: !!shop },
    },
  )
  const productsPayload = (productsData ?? undefined) as CatalogProductsListResponse | undefined
  const products = productsPayload?.products ?? []
  const totalProducts = productsPayload?.total_products ?? null
  const sellingProducts = productsPayload?.selling_products ?? null
  const totalVariants = productsPayload?.total_variants ?? null
  const excludedProducts = (productsPayload?.excluded_products ?? null) !== null
    ? (productsPayload?.excluded_products ?? 0) + excludedDelta
    : null

  // Mutation 훅들
  const deleteCategoryMappingMutation = useDeleteCategoryMapping()
  const saveCategoryMappingMutation = useSaveCategoryMapping()
  const startSyncMutation = useStartSync()
  const toggleProductExcludeMutation = useToggleProductExclude()

  // 매핑되지 않은 카테고리 수 (상품이 있는 카테고리 중 fb_product_category_id가 null)
  const unmappedCategoryCount = (() => {
    // 트리에서 상품이 있고 매핑되지 않은 카테고리를 재귀적으로 카운트
    const countUnmapped = (nodes: Cafe24CategoryTreeNode[]): number => {
      let count = 0
      for (const node of nodes) {
        // product_count가 null/undefined이면 Cafe24 API가 값을 미반환한 것이므로 상품이 있을 수 있다고 가정
        const hasProducts = node.product_count == null || node.product_count > 0
        const isUnmapped =
          node.fb_product_category_id === null && node.inherited_from === null
        if (hasProducts && isUnmapped) {
          count++
        }
        if ((node.children?.length ?? 0) > 0) {
          count += countUnmapped(node.children ?? [])
        }
      }
      return count
    }
    return categoryTree.length > 0 ? countUnmapped(categoryTree) : 0
  })()

  // ==================== 파생 상태 ====================

  // 메타 검증을 거친 동기화 상태 (synced / running / sync_failed /
  // sync_unverified / failed / idle). 배너 분기의 1차 기준.
  const syncStatusValue = normalizeSyncStatus(
    syncStatus?.config.meta_catalog_sync_status,
  )

  // 서버에서 동기화 중인지 (실제 상태)
  const serverSyncRunning = syncStatusValue === 'running'

  // 마지막 동기화 시각 (변경 감지 완료 전에도 syncStatus에서 가져올 수 있음).
  // 주의: synced 가 아닌 상태에서는 메타 검증 전의 값이라 stale 할 수 있다.
  const effectiveLastSyncAt =
    lastSyncAt || syncStatus?.config.meta_catalog_last_sync_at || null

  // UI에서 동기화 중으로 표시할지 (서버 상태 또는 API 호출 중)
  const isSyncRunning = serverSyncRunning || syncPending

  // 마지막 동기화 job (synced 배너의 동기화 건수 표시에 사용)
  const lastJob = syncStatus?.jobs?.[0]

  // ==================== 초기화 & 부수 효과 ====================

  // 인증 확인 (shop 로딩은 레이아웃이 이미 마쳤다)
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // 카탈로그 연동 여부 확인 (config 로드 완료 후)
  useEffect(() => {
    if (!shop || !config) return
    if (!config.meta_catalog_id) {
      setCatalogConnected(false)
      navigate({ to: '/setup/meta-catalog', replace: true })
      return
    }
    setCatalogConnected(true)
  }, [shop, config])

  // Shop 설정 로드 완료 시 브랜드명 동기화
  useEffect(() => {
    if (shopSettings?.default_brand_name !== undefined) {
      setDefaultBrandName(shopSettings.default_brand_name || '')
    }
  }, [shopSettings])

  // 이전 서버 상태 추적 (동기화 완료 감지용)
  const prevServerSyncRunning = useRef<boolean | null>(null)
  const prevLastSyncAt = useRef<string | null>(null)

  // syncPending 해제 함수 (최소 10초 유지)
  const releaseSyncPending = useCallback(() => {
    if (!syncPending || !syncStartedAt) return

    const elapsed = Date.now() - syncStartedAt
    const minDuration = 10000 // 최소 10초 유지

    if (elapsed >= minDuration) {
      setSyncPending(false)
      setSyncStartedAt(null)
    } else {
      setTimeout(() => {
        setSyncPending(false)
        setSyncStartedAt(null)
      }, minDuration - elapsed)
    }
  }, [syncPending, syncStartedAt])

  // syncPending 해제: 서버가 running이 되면 해제 가능
  useEffect(() => {
    if (syncPending && serverSyncRunning) {
      releaseSyncPending()
    }
  }, [syncPending, serverSyncRunning, releaseSyncPending])

  // 동기화 완료 감지: serverSyncRunning이 true->false로 바뀌면 관련 쿼리 무효화
  useEffect(() => {
    const currentLastSyncAt =
      syncStatus?.config.meta_catalog_last_sync_at || null

    const wasRunning = prevServerSyncRunning.current === true
    const isNowIdle = !serverSyncRunning

    const lastSyncAtChanged =
      prevLastSyncAt.current !== null &&
      currentLastSyncAt !== null &&
      prevLastSyncAt.current !== currentLastSyncAt

    if ((wasRunning && isNowIdle) || lastSyncAtChanged) {
      // 동기화 완료 -> 변경사항 & 상품 목록 다시 로드
      queryClient.invalidateQueries({ queryKey: getGetCatalogChangesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() })
      releaseSyncPending()
    }

    prevServerSyncRunning.current = serverSyncRunning
    prevLastSyncAt.current = currentLastSyncAt
  }, [serverSyncRunning, syncStatus, releaseSyncPending, queryClient])

  // ==================== 핸들러 ====================

  // 브랜드명 저장 (brandName 파라미터로 최신값 전달)
  const handleSaveSettings = async (brandName?: string) => {
    const nameToSave = brandName !== undefined ? brandName : defaultBrandName
    try {
      await updateShopSettingsMutation.mutateAsync({
        data: { default_brand_name: nameToSave || null },
      })
      setDefaultBrandName(nameToSave)
      toast.success('저장되었어요. 다음 동기화 시 적용됩니다.')
    } catch (error) {
      console.error('Shop 설정 저장 실패:', error)
      toast.error('저장에 실패했어요.')
    }
  }

  // 카테고리 트리 새로고침 (mutation 후 호출)
  const refreshCategories = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: getGetCafe24CategoriesQueryKey() })
  }, [queryClient])

  // 카테고리 매핑 저장
  const handleSaveCategoryMapping = useCallback(
    async (
      categoryNo: number,
      fbCategoryId: string | null,
      categoryName: string,
      depth: number,
      parentNo: number,
    ) => {
      setSavingCategories((prev) => new Set(prev).add(categoryNo))
      try {
        if (fbCategoryId === null) {
          // 매핑 해제
          await deleteCategoryMappingMutation.mutateAsync({
            cafe24CategoryNo: categoryNo,
          })
          toast.success('매핑이 해제되었어요.')
        } else {
          // 매핑 저장
          await saveCategoryMappingMutation.mutateAsync({
            data: {
              cafe24_category_no: categoryNo,
              cafe24_category_name: categoryName,
              cafe24_category_depth: depth,
              cafe24_parent_category_no: parentNo,
              fb_product_category_id: fbCategoryId,
            },
          })
          toast.success('매핑이 저장되었어요.')
        }
        // 카테고리 트리 다시 로드 (상속 정보 업데이트를 위해)
        refreshCategories()
      } catch (error: unknown) {
        console.error('카테고리 매핑 저장 실패:', error)
        const message =
          (error as { message?: string })?.message || '저장에 실패했어요.'
        toast.error(message)
      } finally {
        setSavingCategories((prev) => {
          const next = new Set(prev)
          next.delete(categoryNo)
          return next
        })
      }
    },
    [deleteCategoryMappingMutation, saveCategoryMappingMutation, refreshCategories],
  )

  // 동기화 요청 핸들러
  const handleSync = (syncType: 'auto' | 'full' | 'incremental') => {
    if (syncType === 'full') {
      setShowFullSyncConfirm(true)
    } else {
      executeSync(syncType)
    }
  }

  // 실제 동기화 실행
  const executeSync = async (syncType: 'auto' | 'full' | 'incremental') => {
    setSyncPending(true)
    setSyncStartedAt(Date.now())
    try {
      await startSyncMutation.mutateAsync({
        data: { sync_type: syncType },
      })
      // API 성공 후 즉시 상태 확인 시작 (폴링이 동기화 완료를 감지)
      queryClient.invalidateQueries({ queryKey: getGetSyncStatusQueryKey() })
    } catch (error: unknown) {
      const err = error as { detail?: string; message?: string }
      const errorMessage = err.detail || err.message || '동기화 시작 실패'
      toast.error(errorMessage)
      setSyncPending(false)
      setSyncStartedAt(null)
    }
  }

  // 전체 동기화 확인 후 실행
  const handleConfirmFullSync = () => {
    setShowFullSyncConfirm(false)
    executeSync('full')
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // 검색 버튼/엔터는 디바운스를 기다리지 않고 즉시 검색한다.
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current)
    setDebouncedSearch(search)
    setOffset(0)
    // URL 파라미터 업데이트 (검색 시 페이지 1로 리셋, 상태 탭은 유지)
    navigate({
      to: '/catalog',
      search: { q: search || undefined, page: undefined, status: statusFilter },
      replace: true,
    })
  }

  // 페이지 변경 핸들러 (URL 파라미터 업데이트 포함)
  const handlePageChange = (newOffset: number) => {
    setOffset(newOffset)
    const newPage = Math.floor(newOffset / limit) + 1
    navigate({
      to: '/catalog',
      search: {
        q: search || undefined,
        page: newPage > 1 ? newPage : undefined,
        status: statusFilter,
      },
      replace: true,
    })
  }

  // 상태 탭 변경 핸들러 (URL 파라미터 연동, 1페이지로 리셋)
  const handleStatusChange = (newStatus?: CatalogStatusFilter) => {
    setOffset(0)
    navigate({
      to: '/catalog',
      search: { q: search || undefined, page: undefined, status: newStatus },
      replace: true,
    })
  }

  // 상품의 실제 제외 여부 (낙관적 토글 오버라이드 우선) — 테이블/카드/안내 공통 사용
  const getIsExcluded = (product: Cafe24ProductResponse): boolean =>
    optimisticExclude.has(product.product_no)
      ? optimisticExclude.get(product.product_no)!
      : (product.override?.exclude_from_catalog ?? false)

  // 상품 제외 토글 핸들러
  const handleToggleExclude = async (product: Cafe24ProductResponse) => {
    const productNo = product.product_no
    const currentExcluded = product.override?.exclude_from_catalog ?? false
    const newExcluded = !currentExcluded

    setTogglingProducts((prev) => new Set(prev).add(productNo))

    try {
      await toggleProductExcludeMutation.mutateAsync({
        productNo,
        data: { exclude: newExcluded },
      })
      // API 성공 → 즉시 로컬 상태 반영 + 토스트
      setOptimisticExclude((prev) => new Map(prev).set(productNo, newExcluded))
      setExcludedDelta((prev) => prev + (newExcluded ? 1 : -1))
      toast.success(
        newExcluded
          ? '광고 노출에서 제외됩니다(카탈로그에는 숨김으로 남아요). 동기화하면 반영됩니다.'
          : '동기화에 포함됩니다. 동기화하면 반영돼요.',
      )
      // 서버 데이터 새로고침 → 완료 후 로컬 오버라이드 제거
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetCatalogChangesQueryKey() }),
      ])
      setOptimisticExclude((prev) => { const next = new Map(prev); next.delete(productNo); return next })
    } catch (error) {
      console.error('제외 토글 실패:', error)
      toast.error('변경에 실패했어요.')
    } finally {
      setTogglingProducts((prev) => {
        const next = new Set(prev)
        next.delete(productNo)
        return next
      })
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원'
  }

  // 레이아웃이 shop 로딩을 마친 뒤에만 하위 라우트를 렌더하므로 여기서 shop 은 항상 있다.
  // (타입 좁히기용 가드 — 상품 목록 로딩은 loadingProducts 가 따로 표시한다)
  if (!shop) {
    return null
  }

  return (
    <>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* 헤더 */}
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-bold text-on-surface">
            메타 카탈로그 상품 관리
          </h1>
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => setShowCategoryMappingModal(true)}
          >
            <FolderTree className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">카테고리 매핑</span>
            {unmappedCategoryCount > 0 && (
              <span className="ml-1 sm:ml-1.5 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                {unmappedCategoryCount}
              </span>
            )}
          </Button>
        </div>

          {/* 기본 브랜드명 인라인 편집 */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-on-surface-variant">기본 브랜드명:</span>
            {editingBrandName ? (
              <div className="flex items-center gap-1.5">
                <Input
                  type="text"
                  value={editBrandNameValue}
                  onChange={(e) => setEditBrandNameValue(e.target.value)}
                  placeholder="브랜드 이름"
                  className="h-7 w-full sm:w-48 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingBrandName(false)
                      // 변경됐을 때만 저장
                      if (editBrandNameValue !== (shopSettings?.default_brand_name || '')) {
                        handleSaveSettings(editBrandNameValue)
                      } else {
                        setDefaultBrandName(editBrandNameValue)
                      }
                    } else if (e.key === 'Escape') {
                      setEditingBrandName(false)
                      setEditBrandNameValue(defaultBrandName)
                    }
                  }}
                />
                <button
                  onClick={() => {
                    setEditingBrandName(false)
                    if (editBrandNameValue !== (shopSettings?.default_brand_name || '')) {
                      handleSaveSettings(editBrandNameValue)
                    } else {
                      setDefaultBrandName(editBrandNameValue)
                    }
                  }}
                  className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                  title="저장"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setEditingBrandName(false)
                    setEditBrandNameValue(defaultBrandName)
                  }}
                  className="p-1 text-outline-variant hover:text-on-surface-variant hover:bg-surface-low rounded"
                  title="취소"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setEditBrandNameValue(defaultBrandName)
                  setEditingBrandName(true)
                }}
                className="flex items-center gap-1.5 text-on-surface hover:text-primary group"
              >
                {/* 설정 로딩 전에는 '미설정'이 잠깐 스쳤다 실제 값으로 바뀌므로,
                    아직 안 온 동안은 자리표시자만 둔다 */}
                <span className={defaultBrandName ? 'font-medium' : 'text-outline-variant'}>
                  {shopSettingsData === undefined
                    ? '—'
                    : defaultBrandName || '미설정'}
                </span>
                <Pencil className="h-3.5 w-3.5 text-outline-variant group-hover:text-primary" />
              </button>
            )}
            {savingSettings && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-outline-variant" />
            )}
            <span className="text-xs text-outline-variant">
              자체브랜드 상품에 적용
            </span>
          </div>

          {/* 카테고리 매핑 경고 배너 (매핑되지 않은 카테고리가 있을 때) */}
          {unmappedCategoryCount > 0 && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start sm:items-center gap-3">
                <FolderTree className="h-5 w-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
                <div>
                  <p className="text-sm font-medium text-primary">
                    카테고리 매핑이 완료되지 않았습니다
                  </p>
                  <p className="text-xs text-primary/80 mt-0.5">
                    {unmappedCategoryCount}개 카테고리가 매핑되지 않았어요. 매핑하면 상품이 더 정확하게 분류돼요.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCategoryMappingModal(true)}
                className="shrink-0 self-start sm:self-auto px-3 py-1.5 text-sm font-medium rounded-md border border-primary/30 text-primary bg-primary/5 hover:bg-primary/15 transition-colors flex items-center"
              >
                <FolderTree className="h-4 w-4 mr-1" />
                카테고리 매핑
              </button>
            </div>
          )}

          {/* 동기화 상태 배너 — meta_catalog_sync_status 6값을 정직하게 렌더.
              syncStatus 로드 완료 전에는 잘못된 안내를 피하려 표시하지 않는다.
              (isSyncRunning 은 버튼 클릭 직후 서버 반영 전 진행 중 표시에도 필요) */}
          {(syncStatus || isSyncRunning) && (
            <CatalogSyncBanner
              status={syncStatusValue}
              lastSyncAt={effectiveLastSyncAt}
              errorMessage={lastJob?.error_message ?? null}
              syncedProducts={lastJob?.synced_products ?? null}
              archivedProducts={lastJob?.archived_products ?? null}
              changesLoaded={changesLoaded}
              syncNeeded={syncNeeded}
              syncReason={syncReason}
              changedCount={changedCount}
              customUnsyncedCount={customUnsyncedCount}
              isSyncRunning={isSyncRunning}
              onSync={handleSync}
            />
          )}

          {/* 카탈로그 개요: 비율 막대 + 상태별 카운트 */}
          {(() => {
            // 카탈로그 전체 기준 카운트 (목록 응답이 함께 내려주는 집계)
            const sellingCount = sellingProducts ?? 0
            // 판매중지 = 전체 - 판매중 (display=T & selling=F). total/selling 둘 다 있을 때만 계산.
            // 품절은 selling=T라 sellingProducts에 포함돼 여기 안 잡힌다 → 라벨은 정확히 '판매중지'.
            // (개요는 집계 응답 기반 싼 정확 카운트, per-product 배지의 '노출제한'(품절+판매중지)과는 별개 개념)
            const notSellingCount =
              totalProducts !== null && sellingProducts !== null
                ? Math.max(0, totalProducts - sellingProducts)
                : 0
            const excludedCount = excludedProducts ?? 0
            const denom = totalProducts ?? 0
            const pct = (n: number) => (denom > 0 ? (n / denom) * 100 : 0)

            if (totalProducts === null) return null
            return (
              <div className="space-y-2">
                {/* 판매중/판매중지 비율 막대 (합=전체) */}
                {denom > 0 && (
                  <div
                    className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-high"
                    role="img"
                    aria-label={`전체 ${denom}개 중 판매중 ${sellingCount}개, 판매중지 ${notSellingCount}개`}
                  >
                    {sellingCount > 0 && (
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${pct(sellingCount)}%` }}
                      />
                    )}
                    {notSellingCount > 0 && (
                      <div
                        className="h-full bg-amber-500"
                        style={{ width: `${pct(notSellingCount)}%` }}
                      />
                    )}
                  </div>
                )}
                {/* 카운트 범례 */}
                <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-sm text-on-surface-variant">
                  <span>
                    총 <strong className="text-on-surface">{denom.toLocaleString()}</strong>개
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                    판매중 <strong className="text-on-surface">{sellingCount.toLocaleString()}</strong>
                  </span>
                  {notSellingCount > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                      판매중지 <strong className="text-on-surface">{notSellingCount.toLocaleString()}</strong>
                    </span>
                  )}
                  {excludedCount > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-outline-variant" />
                      제외 <strong className="text-on-surface">{excludedCount.toLocaleString()}</strong>
                    </span>
                  )}
                </div>
              </div>
            )
          })()}

          {/* 상태 탭 + 검색 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* 상태 탭 (전체/판매중지/제외됨 — 게재불가는 Phase2).
                판매중지 탭은 selling=F만 서버사이드로 거른다(품절은 Phase2).
                따라서 전체 목록에서 '노출제한' 배지(품절 포함)였던 상품이
                판매중지 탭에는 안 보일 수 있다 — 배지는 상태, 탭은 필터로 의미가 다르다. */}
            <Tabs
              value={statusFilter ?? 'all'}
              onValueChange={(v) =>
                handleStatusChange(v === 'all' ? undefined : (v as CatalogStatusFilter))
              }
              className="w-full sm:w-auto"
            >
              <TabsList className="sm:w-auto">
                <TabsTrigger value="all">전체</TabsTrigger>
                <TabsTrigger value="not_selling">판매중지</TabsTrigger>
                <TabsTrigger value="excluded">제외됨</TabsTrigger>
              </TabsList>
            </Tabs>
            {/* 검색 — 제외됨 탭은 백엔드가 검색을 무시하므로(제외 product_no 페이지네이션 우선)
                혼란을 막기 위해 입력을 비활성화하고 안내 문구를 노출한다 (silent 무시 방지) */}
            {(() => {
              const searchDisabled = statusFilter === 'excluded'
              return (
                <form onSubmit={handleSearch} className="flex gap-2 shrink-0">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-outline-variant" />
                    <Input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      disabled={searchDisabled}
                      placeholder={
                        searchDisabled
                          ? '제외됨 탭에서는 검색이 지원되지 않아요'
                          : '상품명 또는 상품번호 검색'
                      }
                      className="pl-10 sm:w-64"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={loadingProducts || searchDisabled}
                  >
                    검색
                  </Button>
                </form>
              )
            })()}
          </div>

          {/* 상태별 대응 안내 박스 — 현재 페이지에 해당 상태가 있을 때만 노출 */}
          {(() => {
            // 현재 페이지 상품들의 표시 상태 (낙관적 제외 반영)
            const pageStatuses = products.map((p) =>
              resolveDisplayStatus(p, getIsExcluded(p)),
            )
            const hasRejected = pageStatuses.some((s) => s.status === 'rejected')
            const hasLimited = pageStatuses.some((s) => s.status === 'limited')
            const hasExcluded = pageStatuses.some((s) => s.status === 'excluded')
            // 품절인데 수동 제외까지 된 상품 수 (재입고 시 자동 노출 안내용)
            const soldOutExcluded = products.filter(
              (p) => p.sold_out === 'T' && getIsExcluded(p),
            ).length

            if (!hasRejected && !hasLimited && !hasExcluded && soldOutExcluded === 0)
              return null

            return (
              <div className="rounded-lg border border-border bg-surface-lowest p-3 space-y-2 text-sm">
                {/* 게재불가 — 고객이 직접 고쳐야 하는 항목이라 가장 위·강조 */}
                {hasRejected && (
                  <div className="flex items-start gap-2 text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-600" />
                    <p>
                      <strong className="font-semibold">게재불가</strong> 상품은 대표이미지가
                      없어 광고에 노출되지 않아요. Cafe24 쇼핑몰 관리자에서 대표이미지를 등록하거나,
                      상품 편집에서 이미지를 지정하면 다음 동기화 때 자동으로 노출돼요.
                    </p>
                  </div>
                )}
                {/* 노출제한 (품절·판매중지) */}
                {hasLimited && (
                  <div className="flex items-start gap-2 text-on-surface-variant">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>
                      <strong className="font-medium text-on-surface">노출제한</strong>(품절·판매중지)
                      상품은 동기화에서 제외하지 않아도 광고에 노출되지 않아요. 재입고하거나 판매를
                      재개하면 자동으로 다시 노출돼요.
                    </p>
                  </div>
                )}
                {/* 수동 제외 */}
                {hasExcluded && (
                  <div className="flex items-start gap-2 text-on-surface-variant">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>
                      <strong className="font-medium text-on-surface">제외됨</strong> 상품은 메타
                      카탈로그에서 '숨김'이 돼요. 광고와 인스타그램·페이스북 샵에는 나오지 않지만,
                      커머스 관리자 제품 목록에는 남아 있는 상태예요. 카페24에 진열 중이라면 메타에서
                      직접 지워도 다음 전체 동기화 때 다시 생겨요. 다시 노출하려면 목록의 '동기화
                      포함'을 켜세요.
                    </p>
                  </div>
                )}
                {/* 품절 + 제외 동시 — 재입고 자동 노출 안내 */}
                {soldOutExcluded > 0 && (
                  <div className="flex items-start gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                    <p>
                      품절 상품 {soldOutExcluded}개가 동기화 제외되어 있어요. 재입고 시 자동으로
                      노출되려면 제외를 해제하세요.
                    </p>
                  </div>
                )}
              </div>
            )
          })()}

          {/* 상품 목록 */}
          <Card>
            <CardContent className="p-0">
              {loadingProducts ? (
                <div className="p-8 text-center text-on-surface-variant">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                  상품을 불러오는 중...
                </div>
              ) : products.length === 0 ? (
                <div className="py-16 px-8 flex flex-col items-center justify-center">
                  {/* 아직 동기화된 적 없는 경우 vs 동기화 후 상품 0건 구분 */}
                  <Package className="h-12 w-12 text-border mb-4" />
                  {!effectiveLastSyncAt ? (
                    <>
                      <p className="text-on-surface font-medium text-base">
                        아직 상품이 동기화되지 않았습니다
                      </p>
                      <p className="text-sm text-on-surface-variant mt-2 max-w-sm text-center">
                        카페24 쇼핑몰의 상품을 메타 카탈로그로 동기화하면
                        여기에 표시됩니다.
                      </p>
                      <Button
                        size="sm"
                        className="mt-5"
                        onClick={() => handleSync('auto')}
                        disabled={isSyncRunning}
                      >
                        {isSyncRunning ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                            동기화 중...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1.5" />
                            동기화 실행
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-outline-variant mt-4 max-w-xs text-center">
                        동기화 전에 카테고리 매핑을 먼저 설정하면 더 정확한
                        분류로 동기화됩니다.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-on-surface font-medium text-base">
                        동기화된 상품이 없습니다
                      </p>
                      <p className="text-sm text-on-surface-variant mt-2 max-w-sm text-center">
                        카페24 쇼핑몰에 판매 중인 상품이 있는지
                        확인해주세요.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <>
                {/* 데스크톱: 테이블 뷰 */}
                <div className="hidden md:block">
                <Table>
                  <TableHeader className="bg-surface">
                    <TableRow>
                      <TableHead className="py-2.5">상품번호</TableHead>
                      <TableHead className="py-2.5">상품명</TableHead>
                      <TableHead className="py-2.5 text-right">가격</TableHead>
                      <TableHead className="py-2.5 text-center">옵션 수</TableHead>
                      <TableHead className="py-2.5 text-center">커스텀 데이터</TableHead>
                      <TableHead className="py-2.5 text-center">동기화 포함</TableHead>
                      <TableHead className="py-2.5 text-center">편집</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                      {products
                        .map((product) => {
                        const isExcluded = getIsExcluded(product)
                        const isToggling = togglingProducts.has(
                          product.product_no,
                        )

                        return (
                          <TableRow
                            key={product.product_no}
                            className={isExcluded ? 'bg-surface opacity-60' : ''}
                          >
                            <TableCell>
                              {product.product_no}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {product.detail_image && (
                                  <img
                                    src={product.detail_image}
                                    alt={product.product_name}
                                    className={`w-10 h-10 object-cover rounded ${isExcluded ? `grayscale` : ``}`}
                                  />
                                )}
                                <div>
                                  <div
                                    className={`text-sm font-medium line-clamp-1 ${isExcluded ? `text-on-surface-variant line-through` : `text-on-surface`}`}
                                  >
                                    {product.product_name}
                                  </div>
                                  <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                    {/* 카탈로그 상태 배지 + 사유 (catalog_status 기반) */}
                                    {(() => {
                                      const { status, reasons } =
                                        resolveDisplayStatus(product, isExcluded)
                                      return (
                                        <CatalogStatusBadge
                                          status={status}
                                          reasons={reasons}
                                        />
                                      )
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums whitespace-nowrap">
                              {(() => {
                                const variants = product.variants ?? []
                                if (variants.length <= 1) return formatPrice(product.price)
                                const prices = variants.map(v => product.price + (v.additional_amount ?? 0))
                                const min = Math.min(...prices)
                                const max = Math.max(...prices)
                                return min === max
                                  ? formatPrice(min)
                                  : `${formatPrice(min)} ~ ${formatPrice(max)}`
                              })()}
                            </TableCell>
                            <TableCell className="text-on-surface-variant text-center">
                              {(product.variants?.length ?? 0) >= 100
                                ? '100+개'
                                : `${product.variants?.length || 1}개`}
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              {product.has_override ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                                  커스텀
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-low text-on-surface-variant">
                                  카페24 원본
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {/* 토글 스위치: ON=동기화됨(녹색), OFF=제외됨(회색) */}
                              <button
                                onClick={() => handleToggleExclude(product)}
                                disabled={isToggling || isSyncRunning}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                                  isExcluded ? 'bg-surface-high' : 'bg-green-500'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                title={
                                  isExcluded
                                    ? '클릭하여 동기화에 포함'
                                    : '클릭하여 카탈로그에서 제외'
                                }
                              >
                                <span
                                  className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full bg-surface-lowest shadow-sm transition-transform ${
                                    isExcluded ? 'translate-x-1' : 'translate-x-6'
                                  }`}
                                >
                                  {isToggling && (
                                    <Loader2 className="h-3 w-3 animate-spin text-outline-variant" />
                                  )}
                                </span>
                              </button>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate({
                                    to: '/catalog/$productNo',
                                    params: {
                                      productNo: String(product.product_no),
                                    },
                                    search: {
                                      q: search || undefined,
                                      page:
                                        currentPage > 1 ? currentPage : undefined,
                                    },
                                  })
                                }
                                disabled={isSyncRunning}
                                title={
                                  isSyncRunning
                                    ? '동기화 진행 중에는 편집할 수 없습니다'
                                    : ''
                                }
                              >
                                편집
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
                </div>

                {/* 모바일: 카드 뷰 */}
                <div className="md:hidden divide-y divide-border">
                  {products.map((product) => {
                    const isExcluded = getIsExcluded(product)
                    const isToggling = togglingProducts.has(product.product_no)

                    // 가격 표시 로직
                    const priceDisplay = (() => {
                      const variants = product.variants ?? []
                      if (variants.length <= 1) return formatPrice(product.price)
                      const prices = variants.map(v => product.price + (v.additional_amount ?? 0))
                      const min = Math.min(...prices)
                      const max = Math.max(...prices)
                      return min === max
                        ? formatPrice(min)
                        : `${formatPrice(min)}~${formatPrice(max)}`
                    })()

                    return (
                      <div
                        key={product.product_no}
                        className={`px-4 py-3 ${isExcluded ? 'opacity-60' : ''}`}
                      >
                        <div className="flex gap-3">
                          {/* 썸네일 */}
                          {product.detail_image && (
                            <img
                              src={product.detail_image}
                              alt={product.product_name}
                              className={`w-14 h-14 object-cover rounded flex-shrink-0 ${isExcluded ? 'grayscale' : ''}`}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            {/* 상품명 + 배지 */}
                            <div className={`text-sm font-medium line-clamp-1 ${isExcluded ? 'text-on-surface-variant line-through' : 'text-on-surface'}`}>
                              {product.product_name}
                            </div>
                            <div className="flex items-center gap-1 flex-wrap mt-0.5">
                              {/* 카탈로그 상태 배지 + 사유 (catalog_status 기반) */}
                              {(() => {
                                const { status, reasons } = resolveDisplayStatus(product, isExcluded)
                                return <CatalogStatusBadge status={status} reasons={reasons} />
                              })()}
                              {product.has_override && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">커스텀</span>
                              )}
                            </div>
                            {/* 가격 · 옵션 수 · 상품번호 */}
                            <div className="flex items-center gap-2 mt-1.5 text-sm">
                              <span className="font-medium tabular-nums text-on-surface">{priceDisplay}</span>
                              <span className="text-border">·</span>
                              <span className="text-on-surface-variant">
                                {(product.variants?.length ?? 0) >= 100 ? '100+' : (product.variants?.length || 1)}옵션
                              </span>
                              <span className="text-border">·</span>
                              <span className="text-on-surface-variant">No.{product.product_no}</span>
                            </div>
                          </div>
                        </div>
                        {/* 하단: 동기화 토글 + 편집 버튼 */}
                        <div className="flex items-center justify-between mt-2.5 pl-[68px]">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-on-surface-variant">동기화</span>
                            <button
                              onClick={() => handleToggleExclude(product)}
                              disabled={isToggling || isSyncRunning}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                                isExcluded ? 'bg-surface-high' : 'bg-green-500'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full bg-surface-lowest shadow-sm transition-transform ${
                                isExcluded ? 'translate-x-1' : 'translate-x-6'
                              }`}>
                                {isToggling && <Loader2 className="h-3 w-3 animate-spin text-outline-variant" />}
                              </span>
                            </button>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate({
                              to: '/catalog/$productNo',
                              params: { productNo: String(product.product_no) },
                              search: { q: search || undefined, page: currentPage > 1 ? currentPage : undefined },
                            })}
                            disabled={isSyncRunning}
                          >
                            편집
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
                </>
              )}

              {/* 페이지네이션 */}
              {products.length > 0 && (
                <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-t bg-surface">
                  <div className="text-xs sm:text-sm text-on-surface-variant">
                    {offset + 1} - {offset + products.length} 표시 중
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handlePageChange(Math.max(0, offset - limit))
                      }
                      disabled={offset === 0 || loadingProducts}
                    >
                      이전
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(offset + limit)}
                      disabled={products.length < limit || loadingProducts}
                    >
                      다음
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      {/* 카테고리 매핑 모달 */}
      <CategoryMappingModal
        open={showCategoryMappingModal}
        onOpenChange={setShowCategoryMappingModal}
        categoryTree={categoryTree}
        loadingCategories={loadingCategories}
        onLoadCategories={refreshCategories}
        onSaveCategoryMapping={handleSaveCategoryMapping}
        savingCategories={savingCategories}
      />
      {/* 전체 동기화 확인 모달 */}
      <Dialog
        open={showFullSyncConfirm}
        onOpenChange={setShowFullSyncConfirm}
        size="sm"
      >
        <DialogContent>
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="text-base font-semibold text-on-surface mb-2">
              전체 동기화
            </h3>
            <p className="text-sm text-on-surface-variant mb-6">
              모든 상품을 다시 동기화합니다.
              <br />
              시간이 오래 걸릴 수 있습니다. 진행하시겠습니까?
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => setShowFullSyncConfirm(false)}
              >
                취소
              </Button>
              <Button variant="primary" onClick={handleConfirmFullSync}>
                전체 동기화 시작
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
